import React from 'react';
import ProjectBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/project/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { DocumentTitle } from '@atlassian/jira-global-document-title/src';
import { useIntl } from '@atlassian/jira-intl';
import type { PreconditionResult as DeploymentsPreconditionResult } from '@atlassian/jira-router-resources-software-deployments/src/types.tsx';
import { getPrecondition } from '@atlassian/jira-software-deployments-precondition/src/ui';
import {
	ViewDeploymentsExperienceStart,
	useViewDeploymentsExperienceFail as useViewDeploymentsExperienceFailDI,
	ViewDeploymentsExpectedErrorExperienceSuccessTracker,
} from '@atlassian/jira-software-deployments/src/services/experience-tracker/index.tsx';
import {
	useViewDeploymentsTimelineExperienceFail,
	ViewDeploymentsTimelineExperienceStart,
} from '@atlassian/jira-software-deployments/src/services/timeline-experience-tracker';
import DeploymentsPageContent from '@atlassian/jira-software-deployments/src/ui/index.tsx';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater';
import { AsyncNoSoftwareLicense } from '@atlassian/jira-spa-apps-common/src/error-screens/no-software-license/async';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication';
import { checkLicense } from '@atlassian/jira-spa-apps-common/src/interceptors/license';
import { useProjectDetails as useProjectDetailsDI } from '@atlassian/jira-spa-apps-software-deployments-common/src/services/project-detail/index.tsx';
import { PreconditionCheck } from '@atlassian/jira-spa-apps-software-deployments-common/src/ui/precondition/index.tsx';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/mark-product-start/index.tsx';
import { useTenantContext as useTenantContextDI } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import messages from './messages';
import { isNetworkError } from './utils';

type Props = {
	useProjectDetails: typeof useProjectDetailsDI;
	useViewDeploymentsExperienceFail: typeof useViewDeploymentsExperienceFailDI;
	useTenantContext: typeof useTenantContextDI;
};

const DeploymentsPage = ({
	useProjectDetails,
	useViewDeploymentsExperienceFail,
	useTenantContext,
}: Props) => {
	const project = useProjectDetails();
	const { formatMessage } = useIntl();
	const onViewDeploymentsExperienceFail = useViewDeploymentsExperienceFail();
	const onViewDeploymentsTimelineExperienceFail = useViewDeploymentsTimelineExperienceFail();
	const tenantContext = useTenantContext();
	const hasSoftwareLicense = checkLicense(tenantContext, 'software');

	let hasDeployments: null | boolean;
	let deploymentsPreconditionData: null | DeploymentsPreconditionResult;

	return (
		<>
			<ViewDeploymentsExperienceStart experienceId={project.key} />
			<MarkProductStart />
			<AuthenticationCheck>
				{hasSoftwareLicense ? (
					<JSErrorBoundary
						id="pageContent"
						packageName="jiraSpaAppsSoftwareDeployments"
						teamName="fusion-solaris"
						extraEventData={{ isSimplifiedProject: project.isSimplified ?? false }}
						onError={(location: string, error: Error) => {
							onViewDeploymentsExperienceFail(location, error, {
								isNetworkError: isNetworkError(error),
								hasDeployments,
							});
							if (
								deploymentsPreconditionData != null &&
								getPrecondition(deploymentsPreconditionData) === 'ALL_SATISFIED'
							) {
								// If the preconditions are satisfied, then we attempted to render the timeline
								onViewDeploymentsTimelineExperienceFail(location, error);
							}
						}}
						attributes={(error: Error) => ({
							isNetworkError: isNetworkError(error),
							isSimplifiedProject: project.isSimplified ?? false,
							hasDeployments,
						})}
					>
						<SubProductUpdater subProduct="software" />
						<ProjectBreadcrumb
							baseUrl={tenantContext.baseUrl}
							projectKey={project.key}
							projectName={project.name ?? ''}
						>
							<PreconditionCheck>
								<ViewDeploymentsTimelineExperienceStart experienceId={project.key} />
								<DeploymentsPageContent projectKey={project.key} />
								<DocumentTitle title={formatMessage(messages.title)} />
							</PreconditionCheck>
						</ProjectBreadcrumb>
					</JSErrorBoundary>
				) : (
					<>
						<AsyncNoSoftwareLicense />
						<ViewDeploymentsExpectedErrorExperienceSuccessTracker />
					</>
				)}
			</AuthenticationCheck>
		</>
	);
};

DeploymentsPage.defaultProps = {
	useProjectDetails: useProjectDetailsDI,
	useViewDeploymentsExperienceFail: useViewDeploymentsExperienceFailDI,
	useTenantContext: useTenantContextDI,
};

export default DeploymentsPage;
